import React from "react";
import { AxisDomainItem } from "recharts/types/util/types";

import { ColorPrimary } from "@intelligems/ig-theme";
import { MetricArrayType } from "@intelligems/ig-types";

import theme from "../../../../theme";
import type {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../../../types/analytics";

import type { GraphConfig } from "./graphConfigs";
import { GraphType } from "./graphConfigs";
import { DecreaseArrow, IncreaseArrow } from "./ImpactArrows";

export const getBarSize = function (
  isImpactGraph: boolean | undefined,
  testGroupLength: number
) {
  if (isImpactGraph) {
    if (testGroupLength <= 3) {
      return 65;
    } else return 35;
  } else if (testGroupLength > 3) {
    return 55;
  } else return 75;
};

export const getTextWidth = function (
  isImpactGraph: boolean,
  testGroupLength: number
) {
  if (isImpactGraph && testGroupLength <= 3) {
    return 75;
  } else return 65;
};

export const getImpact = function (
  groupMetricData: BarMetricData[],
  isArrow: boolean,
  compareGroupA: string,
  compareGroupB: string,
  locale?: string
) {
  const indexA = groupMetricData.findIndex(
    (group: BarMetricData) => group.id === compareGroupA
  );
  const indexB = groupMetricData.findIndex(
    (group: BarMetricData) => group.id === compareGroupB
  );

  if (groupMetricData[indexB]?.value && groupMetricData[indexA]?.value) {
    const impact = calculateImpact(
      groupMetricData[indexB].value,
      groupMetricData[indexA].value,
      locale
    );

    if (isArrow) {
      if (parseInt(impact) > 0) {
        return <IncreaseArrow />;
      } else if (parseInt(impact) < 0) {
        return <DecreaseArrow />;
      } else return null;
    } else {
      if (parseInt(impact) === 0) {
        return "No Change";
      } else return impact;
    }
  }
};

export const calculateImpact = function (
  valueB: number,
  valueA: number,
  locale?: string
) {
  if (valueB < 0 !== valueA < 0) {
    return "N/A";
  } else
    return `${new Intl.NumberFormat(locale || "en", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 1,
    }).format(((valueB - valueA) / Math.abs(valueA)) * 100)}%`;
};

export const findTooltip = function (
  config: GraphConfig,
  metricTooltips: { key: string; value: string }[],
  metricFilterIndex?: number
) {
  const foundIndex = metricTooltips.findIndex(
    (metric) => metric.key === config.title[metricFilterIndex || 0]
  );
  if (foundIndex > -1) {
    return metricTooltips[foundIndex].value;
  } else return null;
};

export const findGroupedGraphTooltip = (
  groupMetricData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[],
  metricTooltips: { key: string; value: string }[]
) => {
  if (groupMetricData.length > 0) {
    const foundIndex = metricTooltips.findIndex(
      (metric) => metric.key === groupMetricData[0].title
    );
    if (foundIndex > -1) {
      return metricTooltips[foundIndex].value;
    } else return null;
  } else return null;
};

export const getEstOrders = function (
  cvrA: number,
  cvrB: number,
  totalTestVisitors: number,
  daysInTest: number
) {
  return Math.round((cvrB - cvrA) * totalTestVisitors * (30 / daysInTest));
};

export const getEstRev = function (
  revPerVisA: number,
  revPerVisB: number,
  totalTestVisitors: number,
  daysInTest: number
) {
  return Math.round(
    (revPerVisB - revPerVisA) * totalTestVisitors * (30 / daysInTest)
  );
};

export const getEstProfit = function (
  profPerVisA: number,
  profPerVisB: number,
  totalTestVisitors: number,
  daysInTest: number
) {
  return Math.round(
    (profPerVisB - profPerVisA) * totalTestVisitors * (30 / daysInTest)
  );
};

export const dataMinFormatter = function (dataMin: AxisDomainItem) {
  if (Math.abs(dataMin as number) === 0) {
    return 0;
  } else if (Math.abs(dataMin as number) <= 0.00001) {
    return -0.1;
  } else if (Math.abs(dataMin as number) > 0.00001 && (dataMin as number) < 0) {
    return "auto";
  } else return 0;
};

export const dataMaxFormatter = function (dataMax: AxisDomainItem) {
  if (Math.abs(dataMax as number) <= 0.00001) {
    return 0.1;
  } else if (Math.abs(dataMax as number) > 0.00001 && (dataMax as number) < 0) {
    return 0;
  } else return "auto";
};

export const getDataMax = (groupMetricData: BarMetricData[]): number => {
  const valueArray = groupMetricData?.map(
    (group: BarMetricData) => group.value as number
  );

  return Math.max(...valueArray);
};
export const getDataMin = function (groupMetricData: BarMetricData[]) {
  const valueArray = groupMetricData.map(
    (group: BarMetricData) => group.value as number
  );

  return Math.min(...valueArray);
};

export const getDataMaxHist = function (
  groupMetricData: HistogramMetricData[]
) {
  const valueArray = groupMetricData.map(
    (group: HistogramMetricData) => group.bucketCount as number
  );

  return Math.max(...valueArray);
};
export const getDataMinHist = function (
  groupMetricData: HistogramMetricData[]
) {
  const valueArray = groupMetricData.map(
    (group: HistogramMetricData) => group.bucketCount as number
  );

  return Math.min(...valueArray);
};

export const getPageBreakpoints = (
  pageSize: string,
  config: GraphConfig,
  innerGrid: boolean
) => {
  if (pageSize === "md") {
    if (
      config.graphType === GraphType.Histogram ||
      config.graphType === GraphType.SyncedBar ||
      config.isFullWidth
    ) {
      return 12;
    } else if (config.isImpactGraph || config.allowThreeRow) {
      return 6;
    } else {
      return 6;
    }
  } else {
    if (
      config.graphType === GraphType.Histogram ||
      config.graphType === GraphType.SyncedBar ||
      config.isFullWidth
    ) {
      return innerGrid ? 6 : 12;
    } else if (config.isImpactGraph || config.allowThreeRow) {
      return 4;
    } else {
      return 6;
    }
  }
};

// Stacked Bar Specific Formatters

// if bar is the top bar of a stacked bar chart OR it is a clustered bar chart => round corners
export const getStackedBarRadius = (
  index: number,
  config: GraphConfig
): [number, number, number, number] => {
  if (
    index === config.stackedBarLegendTitles!.length - 1 ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return [10, 10, 0, 0];
  } else return [0, 0, 0, 0];
};

// if it is a clustered graph, do not stack => different stack ids
export const getStackedBarStackId = (index: number, config: GraphConfig) => {
  if (config.graphType === GraphType.ClusteredBar) {
    return `value${index + 1}`;
  } else return "stackId";
};

export const stackedDataMaxFormatter = (
  config: GraphConfig,
  dataMax: number
) => {
  if (config.autoYaxisMax) {
    return "auto";
  } else if (config.dataTypes[0] === "percent" && !config.addTopPadding) {
    return "dataMax";
  } else if (config.addTopPadding) {
    return dataMax + dataMax * 0.1;
  } else return "auto";
};

export const getStackedDataMax = (
  groupMetricData: StackedBarMetricData[],
  config: GraphConfig
) => {
  const valueArray = groupMetricData.map((data) =>
    (config.stackedBarLegendTitles || []).reduce(
      (prev, curr) => prev + (data[curr] as number),
      0
    )
  );

  return Math.max(...valueArray);
};

export const getStackedDataValue = (
  config: GraphConfig,
  dataValue1: number | undefined,
  dataValue2: number | undefined
) => {
  if (
    config.showSumStackedLabels ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return dataValue1 ? dataValue1 : 0;
  } else {
    const percentage =
      (dataValue1 ? dataValue1 : 0) /
      ((dataValue1 ? dataValue1 : 0) + (dataValue2 ? dataValue2 : 0));
    return percentage || 0;
  }
};

export const valueAccessor = (
  entry: any,
  index: number,
  config: GraphConfig,
  graphValueFormatter: any
) => {
  if (
    index === config.stackedBarLegendTitles!.length - 1 &&
    !config.showAllStackedLabels
  ) {
    return entry
      ? graphValueFormatter(false, entry.value[entry.value.length - 1], config)
      : 0;
  } else return null;
};

export const calculateSumMetric = (
  config: GraphConfig,
  groupMetricData: MetricArrayType,
  graphValueFormatter: (
    isAxisTick: boolean,
    value: number,
    config: GraphConfig,
    index?: number | undefined
  ) => string | undefined,
  metric: string
) => {
  if (Object.keys(groupMetricData).length > 0) {
    const total = groupMetricData[0][metric];
    return graphValueFormatter(false, total as number, config);
  }
  return "";
};

export const groupColors = [
  "#B8D3FF",
  ColorPrimary.light3,
  ColorPrimary.light2,
  theme.palette.primary.light,
  theme.palette.primary.main,
  "#0d47a1",
];

export const stackedBarGroupColors = [
  "#071B53",
  "#0C38B1",
  theme.palette.primary.main,
  "#7EA1FF",
  "#5B74BA",

  "#12585C",
  "#118289",
  "#19BBC6",
  "#17DFED",
  "#9AAEAF",

  "#44198C",
  "#5C23B8",
  "#AC32ED",
  "#D27CFF",
  "#A896B1",
];

export const lineGroupColors = [
  theme.palette.primary.main,
  theme.palette.success.main,
  theme.palette.error.main,
  theme.palette.warning.main,
  "purple",
  "black",
];
